import { adClient } from "../clients/adClient"
import type {
  AdResponseDto,
  AdListParams,
  DeleteAdResponseDto,
  AdPromotionHistoryDto,
  AdRenewalHistoryDto,
} from "../types/ad.types"

/**
 * Ad Service
 * Business logic layer - handles API responses, errors, and side effects
 */
export const adService = {
  /**
   * Get ads feed with filters and pagination
   */
  getAds: async (params?: AdListParams): Promise<{
    data: AdResponseDto[]
    cursor?: string | null
    hasMore?: boolean
  }> => {
    try {
      const response = await adClient.getAds(params)
      
      // Handle different response formats
      // AdListResponseDto is either AdResponseDto[] or {ads: AdResponseDto[], cursor?: string, hasMore?: boolean}
      if (Array.isArray(response)) {
        // Direct array response
        return {
          data: response,
          cursor: null,
          hasMore: false,
        }
      } else if (response.ads && Array.isArray(response.ads)) {
        // Response with {ads: [...]} structure
        return {
          data: response.ads,
          cursor: response.cursor || null,
          hasMore: response.hasMore || false,
        }
      } else {
        // Fallback
        return {
          data: [],
          cursor: null,
          hasMore: false,
        }
      }
    } catch (error: any) {
      throw error
    }
  },

  /**
   * Get single ad by ID
   */
  getAdById: async (id: string): Promise<AdResponseDto> => {
    try {
      const ad = await adClient.getAdById(id)
      return ad
    } catch (error: any) {
      throw error
    }
  },

  /**
   * Delete ad by ID (admin only)
   */
  deleteAd: async (id: string, description: string): Promise<DeleteAdResponseDto> => {
    try {
      const response = await adClient.deleteAd(id, description)
      return response
    } catch (error: any) {
      throw error
    }
  },

  /**
   * Get ad promotion history (admin only)
   */
  getAdPromotions: async (id: string): Promise<AdPromotionHistoryDto[]> => {
    try {
      const response = await adClient.getAdPromotions(id)
      return Array.isArray(response) ? response : []
    } catch (error: any) {
      throw error
    }
  },

  /**
   * Get ad renewal history (admin only)
   */
  getAdRenewals: async (id: string): Promise<AdRenewalHistoryDto[]> => {
    try {
      const response = await adClient.getAdRenewals(id)
      return Array.isArray(response) ? response : []
    } catch (error: any) {
      throw error
    }
  },
}

export default adService

